import { useState, useCallback } from "react";
import { Download, Plus, AlertTriangle, RotateCcw } from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import {
  useDataExports,
  useCreateDataExport,
} from "@/hooks/admin/useEnterprise";
import type {
  DataExportJob,
  ExportFormat,
  ExportStatus,
  CreateExportInput,
} from "@/lib/admin-api/enterprise";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const FORMAT_COLORS: Record<ExportFormat, string> = {
  csv: "bg-emerald-100 text-emerald-800 border-emerald-200",
  json: "bg-blue-100 text-blue-800 border-blue-200",
  xlsx: "bg-amber-100 text-amber-800 border-amber-200",
};

const STATUS_LABELS: Record<ExportStatus, string> = {
  pending: "En attente",
  processing: "En cours",
  ready: "Prêt",
  failed: "Échoué",
};

const STATUS_COLORS: Record<ExportStatus, string> = {
  pending: "bg-stone-100 text-stone-600 border-stone-200",
  processing: "bg-blue-100 text-blue-800 border-blue-200",
  ready: "bg-emerald-100 text-emerald-800 border-emerald-200",
  failed: "bg-red-100 text-red-800 border-red-200",
};

const EXPORTABLE_TABLES: readonly { readonly value: string; readonly label: string }[] = [
  { value: "orders", label: "Commandes" },
  { value: "customers", label: "Clients" },
  { value: "products", label: "Produits" },
  { value: "inventory", label: "Inventaire" },
  { value: "payments", label: "Paiements" },
  { value: "audit_log", label: "Journal d'audit" },
  { value: "deliveries", label: "Livraisons" },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(d: string | null): string {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-SN");
}

// ---------------------------------------------------------------------------
// Create Export Modal
// ---------------------------------------------------------------------------

interface CreateExportModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly isSaving: boolean;
  readonly onSave: (input: CreateExportInput) => Promise<void>;
}

function CreateExportModal({
  open,
  onClose,
  isSaving,
  onSave,
}: CreateExportModalProps) {
  const [name, setName] = useState("");
  const [tables, setTables] = useState<readonly string[]>([]);
  const [format, setFormat] = useState<ExportFormat>("csv");
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setName("");
    setTables([]);
    setFormat("csv");
    setError(null);
    onClose();
  }, [onClose]);

  const toggleTable = useCallback((table: string) => {
    setTables((prev) =>
      prev.includes(table) ? prev.filter((t) => t !== table) : [...prev, table],
    );
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!name.trim()) {
      setError("Le nom est requis.");
      return;
    }
    if (tables.length === 0) {
      setError("Sélectionnez au moins une table.");
      return;
    }
    setError(null);
    try {
      await onSave({ name: name.trim(), tables, format });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Une erreur est survenue.");
    }
  }, [name, tables, format, onSave, handleClose]);

  return (
    <AdminModal
      open={open}
      onClose={handleClose}
      title="Nouvel export de données"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={isSaving}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? "Création…" : "Lancer l'export"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
        )}

        <div>
          <label className="mb-1 block text-sm font-medium text-stone-700">
            Nom <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            placeholder="Ex. : Export mensuel commandes mars 2026"
          />
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-stone-700">
            Tables à exporter <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 gap-2">
            {EXPORTABLE_TABLES.map((t) => (
              <label
                key={t.value}
                className="flex cursor-pointer items-center gap-2 rounded-md border border-stone-200 px-3 py-2 text-sm hover:bg-stone-50"
              >
                <input
                  type="checkbox"
                  checked={tables.includes(t.value)}
                  onChange={() => toggleTable(t.value)}
                  className="rounded border-stone-300 text-amber-600 focus:ring-amber-500"
                />
                <span className="text-stone-700">{t.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-stone-700">Format</label>
          <div className="flex gap-3">
            {(["csv", "json", "xlsx"] as readonly ExportFormat[]).map((f) => (
              <label
                key={f}
                className={`flex cursor-pointer items-center gap-2 rounded-md border px-4 py-2 text-sm font-medium transition-colors ${
                  format === f
                    ? "border-amber-400 bg-amber-50 text-amber-800"
                    : "border-stone-200 bg-white text-stone-700 hover:bg-stone-50"
                }`}
              >
                <input
                  type="radio"
                  name="format"
                  value={f}
                  checked={format === f}
                  onChange={() => setFormat(f)}
                  className="sr-only"
                />
                {f.toUpperCase()}
              </label>
            ))}
          </div>
        </div>
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function DataExport() {
  const [showCreate, setShowCreate] = useState(false);

  const { data, isLoading } = useDataExports();
  const createMutation = useCreateDataExport();

  const jobs = data?.jobs ?? [];

  const handleCreate = useCallback(
    async (input: CreateExportInput) => {
      await createMutation.mutateAsync(input);
    },
    [createMutation],
  );

  const columns: readonly ColumnDef<DataExportJob>[] = [
    {
      id: "name",
      header: "Nom",
      cell: (row) => (
        <span className="font-medium text-stone-900">{row.name}</span>
      ),
    },
    {
      id: "tables",
      header: "Tables",
      cell: (row) => (
        <span className="text-xs text-stone-600">
          {row.tables.join(", ")}
        </span>
      ),
    },
    {
      id: "format",
      header: "Format",
      cell: (row) => (
        <span
          className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold uppercase ${FORMAT_COLORS[row.format]}`}
        >
          {row.format}
        </span>
      ),
    },
    {
      id: "status",
      header: "Statut",
      cell: (row) => (
        <span
          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${STATUS_COLORS[row.status]}`}
        >
          {(row.status === "pending" || row.status === "processing") && (
            <RotateCcw className="h-3 w-3 animate-spin" />
          )}
          {STATUS_LABELS[row.status]}
        </span>
      ),
    },
    {
      id: "row_count",
      header: "Lignes",
      cell: (row) => (
        <span className="text-sm text-stone-600">
          {row.row_count != null ? row.row_count.toLocaleString("fr-SN") : "—"}
        </span>
      ),
    },
    {
      id: "created_at",
      header: "Créé le",
      cell: (row) => (
        <span className="text-xs text-stone-400">{formatDate(row.created_at)}</span>
      ),
    },
    {
      id: "expires_at",
      header: "Expire le",
      cell: (row) => {
        const expired = new Date(row.expires_at) < new Date();
        return (
          <span className={`text-xs ${expired ? "text-red-600 font-medium" : "text-stone-400"}`}>
            {formatDate(row.expires_at)}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: "Télécharger",
      cell: (row) =>
        row.status === "ready" && row.file_url ? (
          <a
            href={row.file_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100"
          >
            <Download className="h-3 w-3" />
            Télécharger
          </a>
        ) : null,
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
              <Download className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Export de données
              </h1>
              <p className="text-sm text-stone-500">
                Exportation des données en CSV, JSON ou XLSX
              </p>
            </div>
          </div>
          <Button onClick={() => setShowCreate(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Nouvel export
          </Button>
        </div>

        {/* Tables info card */}
        <div className="rounded-lg border border-stone-200 bg-white p-4">
          <h3 className="mb-3 text-sm font-semibold text-stone-900">
            Tables disponibles à l'export
          </h3>
          <div className="flex flex-wrap gap-2">
            {EXPORTABLE_TABLES.map((t) => (
              <span
                key={t.value}
                className="inline-flex items-center rounded border border-stone-200 bg-stone-50 px-2.5 py-1 text-xs text-stone-700"
              >
                <code className="mr-1.5 text-stone-500">{t.value}</code>
                {t.label}
              </span>
            ))}
          </div>
        </div>

        {/* Warning */}
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <p className="text-sm text-amber-800">
            <span className="font-semibold">Attention</span> — Les exports expirent
            automatiquement après 7 jours. Téléchargez vos fichiers avant la date
            d'expiration.
          </p>
        </div>

        {/* Table */}
        <AdminDataTable
          columns={columns}
          data={jobs}
          isLoading={isLoading}
          emptyMessage="Aucun export trouvé."
        />
      </div>

      <CreateExportModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        isSaving={createMutation.isPending}
        onSave={handleCreate}
      />
    </AdminLayout>
  );
}
